import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Skeleton,
} from "@mui/material";
import {
  Public,
  Lock,
  Launch,
  MoreVert,
  Circle,
  Restore,
} from "@mui/icons-material";

const statusStyles = {
  ACTIVE: { color: "#10b981", bg: "#d1fae5", label: "Active" },
  PENDING: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
  SUSPENDED: { color: "#ef4444", bg: "#fee2e2", label: "Suspended" },
  MAINTENANCE: { color: "#6366f1", bg: "#e0e7ff", label: "Maintenance" },
  DELETED: { color: "#6b7280", bg: "#f3f4f6", label: "Deleted" },
};

const envStyles = {
  SANDBOX: { color: "#8b5cf6", label: "Sandbox", icon: Lock },
  STAGING: { color: "#f59e0b", label: "Staging", icon: Lock },
  PRODUCTION: { color: "#10b981", label: "Production", icon: Public },
};

const SitesList = ({
  sites,
  loading,
  onMenuOpen,
  onManage,
  onVisit,
  onRestore,
  restoreLoading,
}) => {
  const navigate = useNavigate();
  const protocol =
    process.env.REACT_APP_SSL_ENABLED === "true" ? "https" : "http";

  if (loading && sites.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={72}
            sx={{ borderRadius: 2 }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {sites.map((site) => {
        const status = statusStyles[site.status] || statusStyles.PENDING;
        const env =
          envStyles[site.environment?.toUpperCase()] || envStyles.SANDBOX;
        const EnvIcon = env.icon;
        const domain = site.customDomain || site.domain;
        const isDeleted = site.status === "DELETED";

        return (
          <Box
            key={site.id}
            onClick={() => navigate(`/dashboard/sites/${site.id}`)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              border: "1px solid",
              borderColor: isDeleted ? "error.light" : "divider",
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.15s",
              opacity: isDeleted ? 0.7 : 1,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontWeight: 700,
                fontSize: "1.1rem",
              }}
            >
              {site.name?.[0]?.toUpperCase() || "W"}
            </Box>

            {/* Name + Domain */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.25,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {site.name}
                </Typography>
                <Chip
                  size="small"
                  icon={<EnvIcon sx={{ fontSize: "12px !important" }} />}
                  label={env.label}
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    color: env.color,
                    borderColor: env.color,
                    "& .MuiChip-icon": { color: env.color },
                  }}
                  variant="outlined"
                />
              </Box>
              {domain && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                >
                  {domain}
                </Typography>
              )}
            </Box>

            {/* WP Version */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                alignItems: "center",
                minWidth: 60,
              }}
            >
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: "0.65rem" }}
              >
                WP
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: "0.8rem" }}
              >
                {site.versionInfo?.wpVersion || site.wpVersion || "—"}
              </Typography>
            </Box>

            {/* Storage */}
            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                flexDirection: "column",
                alignItems: "center",
                minWidth: 60,
              }}
            >
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: "0.65rem" }}
              >
                Storage
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ fontSize: "0.8rem" }}
              >
                {site.storageUsedMB
                  ? `${Math.round(site.storageUsedMB)} MB`
                  : "—"}
              </Typography>
            </Box>

            {/* Status */}
            <Chip
              size="small"
              icon={
                <Circle
                  sx={{
                    fontSize: "8px !important",
                    color: `${status.color} !important`,
                  }}
                />
              }
              label={status.label}
              sx={{
                bgcolor: status.bg,
                color: status.color,
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 26,
                minWidth: 90,
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />

            {/* Actions */}
            {isDeleted ? (
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<Restore sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onRestore(site.id);
                }}
                disabled={restoreLoading}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  minWidth: 90,
                  borderRadius: 1.5,
                }}
              >
                Restore
              </Button>
            ) : (
              <>
                {domain && site.status === "ACTIVE" && (
                  <Tooltip title="Visit site">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`${protocol}://${domain}`, "_blank");
                      }}
                      sx={{ color: "text.secondary" }}
                    >
                      <Launch fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="More actions">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMenuOpen(e, site);
                    }}
                    sx={{ color: "text.secondary" }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default SitesList;
